import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Invalidate queries when rows change in the given tables (Supabase Realtime postgres_changes, which applies the
 * user's RLS). Changes are coalesced for `debounceMs` so a 100-beacon record causes one refetch, not 100.
 * Returns whether the channel is currently subscribed (shown as "Live").
 */
export function useLiveInvalidate(
  channelName: string,
  tables: string[],
  queryKeys: QueryKey[],
  debounceMs = 800,
) {
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const keysRef = useRef(queryKeys);
  keysRef.current = queryKeys;
  const tablesKey = tables.join(',');

  useEffect(() => {
    const channel = supabase().channel(channelName);
    for (const table of tablesKey.split(',')) {
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          for (const key of keysRef.current) void queryClient.invalidateQueries({ queryKey: key });
        }, debounceMs);
      });
    }
    channel.subscribe((status) => setLive(status === 'SUBSCRIBED'));
    return () => {
      clearTimeout(timer.current);
      setLive(false);
      void supabase().removeChannel(channel);
    };
  }, [channelName, tablesKey, debounceMs, queryClient]);

  return live;
}
